import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * <pdp-main> — drives the PDP v1 section (sections/pdp-main.liquid):
 * - gallery: scroll-snap slider synced with thumbnails
 * - variant picker: swaps price / availability / hidden variant id / gallery slide
 * - quantity stepper
 * - add to cart: posts to /cart/add and dispatches CartLinesUpdateEvent so the theme's
 *   cart drawer, cart bubble and cart items refresh exactly like the native product form.
 */
class PdpMain extends HTMLElement {
  connectedCallback() {
    this.slides = this.querySelector('[data-pdp-slides]');
    this.thumbs = [...this.querySelectorAll('[data-pdp-thumb]')];
    this.form = this.querySelector('[data-pdp-form]');
    this.variants = JSON.parse(this.querySelector('[data-pdp-variants]')?.textContent || '[]');

    this.#initGallery();
    this.#initVariantPicker();
    this.#initQuantity();
    this.#initBreakdown();
    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  /* ---------- Break it down popups ---------- */

  // Each row's button opens its own <dialog>; the close button or a tap on the backdrop
  // (outside the dialog's box) closes it.
  #initBreakdown() {
    this.querySelectorAll('[data-breakdown-open]').forEach((button) => {
      const dialog = this.querySelector(`#${CSS.escape(button.dataset.breakdownOpen)}`);
      if (!dialog) return;

      button.addEventListener('click', () => dialog.showModal());
      dialog.querySelector('[data-breakdown-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;
        if (!inside) dialog.close();
      });
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /* ---------- Gallery ---------- */

  #initGallery() {
    if (!this.slides) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goToSlide(Number(thumb.dataset.index)));
    });

    this.querySelectorAll('[data-pdp-arrow]').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const step = arrow.dataset.pdpArrow === 'next' ? 1 : -1;
        const count = this.slides.children.length;
        this.goToSlide((this.activeIndex + step + count) % count);
      });
    });

    this.activeIndex = 0;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.#setActive(Number(entry.target.dataset.index));
        });
      },
      { root: this.slides, threshold: 0.6 }
    );
    [...this.slides.children].forEach((slide) => this.observer.observe(slide));
  }

  goToSlide(index) {
    const slide = this.slides?.children[index];
    if (!slide) return;
    this.slides.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.#setActive(index);
  }

  #setActive(index) {
    this.activeIndex = index;
    this.thumbs.forEach((thumb) => {
      const active = Number(thumb.dataset.index) === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
    this.querySelectorAll('[data-pdp-dot]').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  /* ---------- Variants ---------- */

  #initVariantPicker() {
    this.querySelectorAll('[data-pdp-option] input').forEach((input) => {
      input.addEventListener('change', () => this.#onOptionChange());
    });
  }

  #onOptionChange() {
    const selected = [...this.querySelectorAll('[data-pdp-option]')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value
    );
    this.querySelectorAll('[data-pdp-option]').forEach((fieldset) => {
      const value = fieldset.querySelector('input:checked')?.value;
      const label = fieldset.querySelector('[data-pdp-option-value]');
      if (label) label.textContent = value;
    });

    const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
    this.#updateVariant(variant);
  }

  #updateVariant(variant) {
    const button = this.querySelector('[data-pdp-add]');
    const buttonText = this.querySelector('[data-pdp-add-text]');

    if (!variant) {
      if (button) button.disabled = true;
      if (buttonText) buttonText.textContent = button?.dataset.unavailable;
      return;
    }

    const idInput = this.form?.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;

    const price = this.querySelector('[data-pdp-price]');
    const compare = this.querySelector('[data-pdp-compare]');
    const save = this.querySelector('[data-pdp-save]');
    const onSale = variant.compare_at_price > variant.price;

    if (price) price.textContent = variant.price_formatted;
    if (compare) {
      compare.textContent = onSale ? variant.compare_at_price_formatted : '';
      compare.hidden = !onSale;
    }
    if (save) {
      const percent = onSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
      // [amount] matches snippets/pdp-money.liquid: "₹300", no ".00".
      const amount = onSale
        ? '₹' + ((variant.compare_at_price - variant.price) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })
        : '';
      save.textContent = save.dataset.template.replace('[percent]', percent).replace('[amount]', amount);
      save.hidden = !onSale;
    }

    if (button) button.disabled = !variant.available;
    if (buttonText) buttonText.textContent = variant.available ? button.dataset.add : button.dataset.soldOut;

    if (variant.media_id) {
      const slide = this.slides?.querySelector(`[data-media-id="${variant.media_id}"]`);
      if (slide) this.goToSlide(Number(slide.dataset.index));
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /* ---------- Quantity ---------- */

  #initQuantity() {
    const input = this.querySelector('[data-pdp-qty-input]');
    if (!input) return;
    this.querySelectorAll('[data-pdp-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const step = btn.dataset.pdpQty === 'plus' ? 1 : -1;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      });
    });
  }

  /* ---------- Add to cart ---------- */

  #handleSubmit = async (event) => {
    event.preventDefault();
    const button = this.querySelector('[data-pdp-add]');
    const error = this.querySelector('[data-pdp-error]');
    if (!button || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    const formData = new FormData(this.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      }).then((res) => res.json());

      if (response.status) {
        deferred.reject(new Error(response.description || response.message));
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (error) {
          error.textContent = response.description || response.message;
          error.hidden = false;
        }
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'pdp-main',
          sourceId: this.id,
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (err) {
      console.error(err);
      deferred.reject(err);
      this.dispatchEvent(new CartErrorEvent({ error: err?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      const idInput = this.form.querySelector('input[name="id"]');
      const variant = this.variants.find((v) => String(v.id) === idInput?.value);
      button.disabled = variant ? !variant.available : false;
    }
  };
}

if (!customElements.get('pdp-main')) customElements.define('pdp-main', PdpMain);
